import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button, Col, Form, FormGroup, Label, Row } from "reactstrap";
import { yupResolver } from "@hookform/resolvers/yup";
import ValidateSchema from "./ValidateSchema";
import Select from "react-select";
import MessageFormValidate from "components/shared-components/MessageFormValidate";
import FormInput from "components/Common/Form/FormInput";
import { DatePicker } from "antd";
import useLocationData from "./useLocationData";
import FormHubSelect from "components/Common/Form/FormHubSelect";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import useMasterData from "./useMasterData";
import { useCreateOrder, useOrderStatus } from "hooks/order/useOrder";
import CreateStatusNotification from "./CreateStatusNotification";
import { checkIsInFixedDaysOff } from "helpers/utils";
import { filterInvalidHolidays, isDayInInvalidHolidays } from "./utils";

export const BLOOD_COLLECTION_ORDER_FORM_ROLE_MODE = {
  ADMIN: "ADMIN",
  SALE: "SALE",
};

export const BLOOD_COLLECTION_ORDER_FORM_MODE = {
  DETAIL: "DETAIL",
  CREATE: "CREATE",
};

const BloodCollectionOrderForm = ({ roleMode, formMode, orderDetail }) => {
  const { t } = useTranslation(["bloodOrder"]);
  const navigate = useNavigate();
  const [statusNotification, setStatusNotification] = useState({
    visible: false,
    status: "",
    success_order_code: "",
  });
  const [hubIdDefault, setHubIdDefault] = useState(undefined);
  const [keywordCustomer, setKeywordCustomer] = useState("");
  let timeSearchCustomer;
  const isViewOnlyMode = formMode === BLOOD_COLLECTION_ORDER_FORM_MODE.DETAIL;
  const { refetch: fetchOrderStatusList, data: orderStatusList } =
    useOrderStatus();

  const mainForm = useForm({
    resolver: yupResolver(ValidateSchema(t, roleMode)),
    mode: "all",
    reValidateMode: "onChange",
  });

  const {
    formState: { errors },
    control,
    getValues,
    register,
    trigger,
    watch,
  } = mainForm;

  const actionDateWatch = watch("action_date");
  const customerWatch = watch("customer");

  const {
    provinceOptions,
    onChangeProvince,
    // districtOptions,
    // onChangeDistrict,
    // isDisableDistrictSelect,
    wardOptions,
    onChangeWard,
    isDisableWardSelect,
  } = useLocationData({ mainForm });

  const {
    collectorLocationHub,
    customers,
    testPackList,
    isTestPackLoading,
    paymentMethodList,
    timeslotOptions,
    listSale,
    isLoadingListSale,
    holidaysHub,
    timeslotList,
  } = useMasterData({ mainForm, keywordCustomer, isViewOnlyMode, roleMode });

  const onCreateOrderSuccess = responseData => {
    setStatusNotification({
      visible: true,
      status: "success",
      success_order_code: responseData.order_code,
    });
  };

  const onCreateOrderError = () => {
    setStatusNotification({
      visible: true,
      status: "error",
      success_order_code: "",
    });
  };

  const { mutate: createOrder, isLoading: isCreateOrderLoading } =
    useCreateOrder(onCreateOrderSuccess, onCreateOrderError);

  const onSubmitForm = () => {
    trigger().then(isValid => {
      if (isValid) {
        const formValues = getValues();
        const customerValue = JSON.parse(formValues.customer.value);
        const bodyData = {
          //default value
          type: 3,
          collect_money: 0,
          transport_id: null,
          runner_id: null,
          bus_id: null,
          resources: { bill: [null], package: [null] },
          shipping_type: 4,
          //default value
          customer_id: customerValue.id,
          customer_code: customerValue.code,
          product_id: formValues.test_pack.value,
          customer_address: formValues.detail_address,
          customer_province_code: formValues.province_code.value,
          customer_district_code: formValues.ward_code.value,
          // customer_ward_code: formValues.ward_code.value,
          customer_contact_name: formValues.customer_name,
          customer_contact_phone: formValues.phone,
          from_hub: formValues.from_hub.value,
          payment_method_id: formValues.payment_method.value,
          action_date: moment(formValues.action_date).format("YYYY-MM-DD"),
          timeslot_id: formValues.timeslot.value,
          is_grab: formValues.is_grab ? 1 : 0,
          sale_id: formValues.sale.value,
          note: formValues.note,
        };
        createOrder(bodyData);
        console.log(onSubmitForm);
      }
    });
  };

  useEffect(() => {
    if (isViewOnlyMode && orderDetail) {
      mainForm.reset({
        customer: { label: orderDetail.customer_name },
        test_pack: { label: orderDetail.product_name },
        full_address: orderDetail.sender_full_address,
        customer_name: orderDetail.sender_contact_name,
        phone: orderDetail.sender_contact_phone,
        payment_method: { label: orderDetail.payment_method_name },
        action_date: moment(orderDetail.action_date),
        timeslot: {
          label: orderDetail.time_slot_name,
          value: orderDetail.timeslot_id,
        },
        sale: { label: orderDetail.sale_name, value: orderDetail.sale_id },
        note: orderDetail.note,
        status:
          orderStatusList.find(
            item => item.value === String(orderDetail.status)
          )?.label || "",
        updated_time: moment(parseInt(orderDetail.checker_date) * 1000).format(
          "YYYY-MM-DD HH:mm:ss"
        ),
        from_hub: {
          label:
            orderDetail?.reciever_name ||
            orderDetail?.receiver_info?.name ||
            String(orderDetail?.from_hub || ""),
          value: orderDetail?.from_hub,
        },
      });
      setHubIdDefault(orderDetail.from_hub);
    }
  }, [orderDetail, orderStatusList]);

  useEffect(() => {
    if (orderDetail) {
      fetchOrderStatusList();
    }
  }, [orderDetail]);

  const handleDisabledDate = ({
    current,
    fixed_days_off,
    holidays,
    timeslots,
  }) => {
    if (current) {
      const isThePastDay = current < moment().startOf("day");
      const isFixedDaysOff = checkIsInFixedDaysOff(fixed_days_off, current);

      const listInvalidHolidays = filterInvalidHolidays(timeslots, holidays);
      const isNotValidHoliday = isDayInInvalidHolidays(
        listInvalidHolidays,
        current
      );

      return isThePastDay || isFixedDaysOff || isNotValidHoliday;
    }

    return true;
  };
  return (
    <>
      <Form>
        <Row>
          <Col md={7} xs={12}>
            <Row>
              <Col md={6} xs={12}>
                <FormGroup>
                  <Label htmlFor="customer">
                    {t("label.customer_id")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={control}
                    name="customer"
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder={t("placeholder.select", {
                          target: t("label.customer_id"),
                        })}
                        options={
                          customers?.items?.map(item => ({
                            label: item.name,
                            value: JSON.stringify({
                              id: item.id,
                              code: item.code,
                            }),
                          })) || []
                        }
                        classNamePrefix="select"
                        isSearchable
                        className="basic-single w-100"
                        isDisabled={isViewOnlyMode}
                        onInputChange={(keywords, actions) => {
                          clearTimeout(timeSearchCustomer);
                          timeSearchCustomer = setTimeout(() => {
                            if (actions.action === "input-change") {
                              setKeywordCustomer(keywords);
                            }
                          }, 500);
                        }}
                      />
                    )}
                  />
                  {errors.customer && (
                    <MessageFormValidate error={errors.customer.message} />
                  )}
                </FormGroup>
              </Col>
              <Col md={6} xs={12}>
                <FormGroup>
                  <Label htmlFor="test_pack">
                    {t("label.test_pack")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={control}
                    name="test_pack"
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder={t("placeholder.select", {
                          target: t("label.test_pack"),
                        })}
                        options={
                          testPackList?.items?.map(item => ({
                            label: item.name,
                            value: item.id,
                          })) || []
                        }
                        classNamePrefix="select"
                        isSearchable
                        className="basic-single w-100"
                        isLoading={isTestPackLoading}
                        isDisabled={isTestPackLoading || isViewOnlyMode}
                      />
                    )}
                  />
                  {errors.test_pack && (
                    <MessageFormValidate error={errors.test_pack.message} />
                  )}
                </FormGroup>
              </Col>
              <Col xs={12} md={12}>
                <Label>
                  {t("label.delivery_address")}{" "}
                  <span className="text-danger">*</span>
                </Label>
                {isViewOnlyMode && (
                  <FormGroup>
                    <FormInput
                      isLabel={false}
                      t={t}
                      fieldName="full_address"
                      placeholder={" "}
                      type="text"
                      mainForm={mainForm}
                      isFormControlStyle={false}
                      isDisabled={true}
                    />
                  </FormGroup>
                )}
              </Col>
              {!isViewOnlyMode && (
                <>
                  <Col xs={12} md={6}>
                    <FormGroup>
                      <FormInput
                        isLabel={false}
                        t={t}
                        fieldName="detail_address"
                        placeholder={t("placeholder.detail_address")}
                        type="text"
                        mainForm={mainForm}
                        isFormControlStyle={false}
                      />
                    </FormGroup>
                  </Col>
                  <Col xs={12} md={6}>
                    <FormGroup>
                      <Controller
                        control={control}
                        name="province_code"
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder={t("placeholder.province")}
                            options={provinceOptions}
                            onChange={onChangeProvince}
                            isSearchable
                            className="basic-single w-100"
                            classNamePrefix="select"
                          />
                        )}
                      />
                      {errors.province_code && (
                        <MessageFormValidate
                          error={errors.province_code.message}
                        />
                      )}
                    </FormGroup>
                  </Col>
                  <Col xs={12} md={6}>
                    <FormGroup>
                      <Controller
                        control={control}
                        name="ward_code"
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder={t("placeholder.ward")}
                            options={wardOptions}
                            onChange={onChangeWard}
                            isSearchable
                            className="basic-single w-100"
                            classNamePrefix="select"
                            isDisabled={isDisableWardSelect}
                          />
                        )}
                      />
                      {errors.ward_code && (
                        <MessageFormValidate error={errors.ward_code.message} />
                      )}
                    </FormGroup>
                  </Col>
                  {/* <Col xs={12} md={6}>
                    <FormGroup>
                      <Controller
                        control={control}
                        name="ward_code"
                        render={({ field }) => (
                          <Select
                            {...field}
                            placeholder={t("placeholder.ward")}
                            options={wardOptions}
                            onChange={onChangeWard}
                            isSearchable
                            className="basic-single w-100"
                            classNamePrefix="select"
                            isDisabled={isDisableWardSelect}
                          />
                        )}
                      />
                      {errors.ward_code && (
                        <MessageFormValidate error={errors.ward_code.message} />
                      )}
                    </FormGroup>
                  </Col> */}
                </>
              )}

              <Col xs={12} md={6}>
                <FormGroup>
                  <FormInput
                    isLabel={false}
                    t={t}
                    fieldName="customer_name"
                    placeholder={t("placeholder.customer_name")}
                    type="text"
                    mainForm={mainForm}
                    isFormControlStyle={false}
                    isDisabled={isViewOnlyMode}
                  />
                </FormGroup>
              </Col>
              <Col xs={12} md={6}>
                <FormGroup>
                  <FormInput
                    isLabel={false}
                    t={t}
                    fieldName="phone"
                    placeholder={t("placeholder.phone")}
                    type="text"
                    mainForm={mainForm}
                    isFormControlStyle={false}
                    isDisabled={isViewOnlyMode}
                  />
                </FormGroup>
              </Col>
              {/* Empty Form */}
              {!isViewOnlyMode && (
                <Col
                  className="d-none d-md-block"
                  md={6}
                  style={{ visibility: "hidden" }}
                >
                  <FormGroup>
                    <div style={{ height: "38px" }}></div>
                  </FormGroup>
                </Col>
              )}
              {roleMode === BLOOD_COLLECTION_ORDER_FORM_ROLE_MODE.ADMIN && (
                <Col md={6} xs={12}>
                  <FormHubSelect
                    isRequired
                    isMyHubOnly
                    mainForm={mainForm}
                    fieldName="from_hub"
                    t={t}
                    title={t("label.receive_hub_id")}
                    hubIdDefault={hubIdDefault}
                    isDisabled={true}
                  />
                </Col>
              )}

              <Col md={6} xs={12}>
                <FormGroup>
                  <Label htmlFor="payment_method">
                    {t("label.payment_method")}{" "}
                    <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    control={control}
                    name="payment_method"
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder={t("placeholder.select", {
                          target: t("label.payment_method"),
                        })}
                        options={
                          paymentMethodList?.items?.map(item => ({
                            label: item.name,
                            value: item.id,
                          })) || []
                        }
                        classNamePrefix="select"
                        isSearchable
                        className="basic-single w-100"
                        isDisabled={
                          !collectorLocationHub?.hub_id || isViewOnlyMode
                        }
                      />
                    )}
                  />
                  {errors.payment_method && (
                    <MessageFormValidate
                      error={errors.payment_method.message}
                    />
                  )}
                </FormGroup>
              </Col>
              <Col md={12} xs={12}>
                <Label htmlFor="action_date">
                  {t("label.action_date")}{" "}
                  <span className="text-danger">*</span>
                </Label>
              </Col>
              <Col md={6} xs={12}>
                <FormGroup>
                  <Controller
                    control={control}
                    name="action_date"
                    render={({ field }) => (
                      <DatePicker
                        {...field}
                        className="w-100"
                        placeholder={t("placeholder.select", {
                          target: t("label.date"),
                        })}
                        style={{
                          height: 38,
                        }}
                        disabled={
                          !collectorLocationHub?.hub_id || isViewOnlyMode
                        }
                        disabledDate={current =>
                          handleDisabledDate({
                            current,
                            fixed_days_off:
                              collectorLocationHub?.hub?.fixed_days_off,
                            holidays: holidaysHub?.items,
                            timeslots: timeslotList?.items,
                          })
                        }
                      />
                    )}
                  />
                  {errors.action_date && (
                    <MessageFormValidate error={errors.action_date.message} />
                  )}
                </FormGroup>
              </Col>
              <Col md={6} xs={12}>
                <FormGroup>
                  <Controller
                    control={control}
                    name="timeslot"
                    render={({ field }) => (
                      <Select
                        {...field}
                        placeholder={t("placeholder.select", {
                          target: t("label.timeslot"),
                        })}
                        options={timeslotOptions}
                        classNamePrefix="select"
                        isSearchable
                        className="basic-single w-100"
                        isDisabled={
                          !collectorLocationHub?.hub_id ||
                          isViewOnlyMode ||
                          !actionDateWatch
                        }
                      />
                    )}
                  />
                  {errors.timeslot && (
                    <MessageFormValidate error={errors.timeslot.message} />
                  )}
                </FormGroup>
              </Col>
              <Col xs={6}>
                <FormGroup
                  className="orders-card d-flex align-items-center"
                  style={{ padding: "12px" }}
                >
                  <input
                    name="is_grab"
                    type="checkbox"
                    className="mt-0"
                    {...register("is_grab")}
                  />
                  <Label
                    check
                    htmlFor="is-grab"
                    style={{
                      marginLeft: "8px",
                      fontWeight: "400",
                    }}
                  >
                    {t("label.is_grab")}
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          </Col>
          <Col md={5} xs={12}>
            <Row>
              {roleMode === BLOOD_COLLECTION_ORDER_FORM_ROLE_MODE.ADMIN && (
                <Col xs={12}>
                  <FormGroup>
                    <Label htmlFor="sale">
                      {t("label.sale")} <span className="text-danger">*</span>
                    </Label>
                    <Controller
                      control={control}
                      name="sale"
                      render={({ field }) => (
                        <Select
                          {...field}
                          placeholder={t("placeholder.select", {
                            target: t("label.sale"),
                          })}
                          options={
                            listSale?.data?.items?.map(item => ({
                              label: item.display_name,
                              value: item.id,
                            })) || []
                          }
                          classNamePrefix="select"
                          isSearchable
                          className="basic-single w-100"
                          isLoading={isLoadingListSale}
                          isDisabled={
                            !customerWatch ||
                            isLoadingListSale ||
                            isViewOnlyMode
                          }
                        />
                      )}
                    />
                    {errors.sale && (
                      <MessageFormValidate error={errors.sale.message} />
                    )}
                  </FormGroup>
                </Col>
              )}

              <Col xs={12}>
                <FormGroup>
                  <Label htmlFor="note">{t("label.note")}</Label>
                  <textarea
                    type="textarea"
                    className={`form-control`}
                    name="note"
                    placeholder={t("placeholder.note")}
                    rows={6}
                    style={{
                      height: 170,
                    }}
                    disabled={isViewOnlyMode}
                    {...register("note")}
                  />
                </FormGroup>
              </Col>
              {isViewOnlyMode && (
                <>
                  <Col md={6}>
                    <FormGroup>
                      <Label htmlFor="status">{t("label.status")}</Label>
                      <FormInput
                        isLabel={false}
                        t={t}
                        fieldName="status"
                        placeholder={" "}
                        type="text"
                        mainForm={mainForm}
                        isFormControlStyle={false}
                        isDisabled={isViewOnlyMode}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label htmlFor="updated_time">
                        {t("label.updated_time")}
                      </Label>
                      <FormInput
                        isLabel={false}
                        t={t}
                        fieldName="updated_time"
                        placeholder={" "}
                        type="text"
                        mainForm={mainForm}
                        isFormControlStyle={false}
                        isDisabled={isViewOnlyMode}
                      />
                    </FormGroup>
                  </Col>
                </>
              )}
            </Row>
          </Col>
          {!isViewOnlyMode && (
            <Col md={7} xs={12}>
              <div className="d-flex justify-content-between">
                <Button
                  color="danger"
                  className="border-0 d-flex align-items-center gap-1"
                  size="xs"
                  onClick={() => navigate(-1)}
                >
                  <i className="fas fa-undo label-icon fs-6"></i>
                  {t("button.cancel")}
                </Button>
                <Button
                  color="primary"
                  className="border-0 d-flex align-items-center gap-1"
                  size="xs"
                  onClick={onSubmitForm}
                  disabled={isCreateOrderLoading}
                >
                  <i className="bx bx-save label-icon fs-4"></i>
                  {t("button.submit")}
                </Button>
              </div>
            </Col>
          )}
        </Row>
      </Form>
      <CreateStatusNotification
        isOpen={statusNotification.visible}
        status={statusNotification.status}
        successOrderCode={statusNotification.success_order_code}
        toggleNotification={() =>
          setStatusNotification(prev => ({
            ...prev,
            visible: false,
          }))
        }
      />
    </>
  );
};

export default BloodCollectionOrderForm;
